<template>
  <div class="contact-wrapper">
    <div v-if="loading" class="loading"><img src="../assets/spinner.svg" /></div>
    <div v-else-if="data" class="contact-content">
      <CompanyInformation v-if="type === 'company'" :company="data" :showValidationErrors="showValidationErrors" />
      <IndividualInformation v-else-if="type === 'contact'" :contact="data" :showValidationErrors="showValidationErrors" @showLinkCompany="showLinkCompany = true" @handleUnlink="confirmUnlinkCompany" />

      <Status :type="type" :entity="data" :showValidationErrors="showValidationErrors" />
    </div>

    <template v-if="!loading && !error && entityid !== 'new'">
      <nav class="tabs">
        <a class="left-space"></a>
        <a :class="{ active: activeTab === 'linkedContacts' }" v-if="type === 'company'" @click="activeTab = 'linkedContacts'">Linked Contacts</a>
        <a :class="{ active: activeTab === 'diary' }" @click="activeTab = 'diary'">Diary/Notes</a>
        <a :class="{ active: activeTab === 'leads' }" @click="activeTab = 'leads'">Leads</a>
        <a :class="{ active: activeTab === 'quotes' }" v-if="user.showQuotes" @click="activeTab = 'quotes'">Quotes</a>
        <a :class="{ active: activeTab === 'reminders' }" @click="activeTab = 'reminders'">Reminders</a>
        <a :class="{ active: activeTab === 'files' }" @click="activeTab = 'files'">Files</a>
        <a class="fill buttons-container">
          <button v-if="activeTab === 'linkedContacts' && hasCheckedEmails" class="add-square-btn-small" @click="$refs.linkedContactsTab?.copyCheckedEmails()">Copy</button>
          <button v-if="activeTab === 'linkedContacts' && hasCheckedEmails" class="add-square-btn-small" @click="$refs.linkedContactsTab?.emailCheckedContacts()">Email</button>
          <div class="add-contact-btn" @click="showAddContactMenu = true" v-click-outside="closeAddContactMenu" v-if="activeTab === 'linkedContacts'">
            <button class="add-square-btn-small"><img src="../assets/plus-symbol.svg" /> Add Linked Contact</button>
            <div class="add-contact-menu z-index-10-fg dropdown" v-if="showAddContactMenu">
              <ul>
                <li @click="this.$router.push({ name: 'contacts', params: { contactid: 'new' }, query: { linkedCompanyId: this.entityid, linkedCompanyName: this.data.NAME } })">Create & Link Contact</li>
                <li @click="linkExistingContact">Link Existing Contact</li>
              </ul>
            </div>
          </div>
          <button v-if="activeTab === 'leads' && user.showLeads" class="add-square-btn-small" @click="addLead"><img src="../assets/plus-symbol.svg" /> Add Lead</button>
          <button v-if="activeTab === 'quotes' && user.showQuotes" class="add-square-btn-small" @click="addQuote"><img src="../assets/plus-symbol.svg" /> Add Quote</button>
          <button v-if="activeTab === 'diary'" class="add-square-btn-small" @click="addDiaryNote"><img src="../assets/plus-symbol.svg" /> Add Note</button>
          <button v-if="activeTab === 'reminders'" class="add-square-btn-small" @click="addReminder"><img src="../assets/plus-symbol.svg" /> Add Reminder</button>
          <button v-if="activeTab === 'files'" class="add-square-btn-small" @click="addFiles"><img src="../assets/plus-symbol.svg" /> Add File</button>
        </a>
      </nav>

      <!-- LinkedContactsTab -->
      <LinkedContactsTab v-if="activeTab === 'linkedContacts' && type === 'company' && entityid" :companyid="entityid" ref="linkedContactsTab" @emailCheckboxStateChanged="hasCheckedEmails = $event" @contactStatusChanged="handleContactStatusChanged" />

      <!-- DiaryTab -->
      <DiaryTab v-if="activeTab === 'diary' && entityid && entityid !== 'new'" :type="type" :entityid="entityid" ref="diaryTab" :contacts="data.contacts" />

      <!-- LeadsTab -->
      <LeadsTab v-if="activeTab === 'leads' && entityid && entityid !== 'new' && user.showLeads" :type="type" :entityid="entityid" ref="leadsTab" />

      <!-- QuotesTab -->
      <QuotesTab v-if="activeTab === 'quotes' && entityid && entityid !== 'new' && user.showQuotes" :type="type" :entityid="entityid" ref="quotesTab" />

      <!-- RemindersTab -->
      <RemindersTab v-if="activeTab === 'reminders' && entityid && entityid !== 'new'" :type="type" :entityid="entityid" ref="remindersTab" />

      <!-- FilesTab -->
      <FilesTab v-if="activeTab === 'files' && entityid && entityid !== 'new'" :type="type" :entityid="entityid" ref="filesTab" />
    </template>

    <!-- Link Contact to Company Popup -->
    <LinkContactToCompany v-if="showLinkCompany" @link="handleLinkCompany" @cancel="showLinkCompany = false" />

    <!-- Unlink Confirmation -->
    <Confirmation
      v-if="showUnlinkConfirm"
      @confirm="handleUnlinkCompany"
      @cancel="showUnlinkConfirm = false"
      header="Unlink Company"
      message="This will remove the company link from this contact. Are you sure?"
      confirmText="Unlink"
      cancelText="Cancel"
      :serious="true" />
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import useContactQuery from '../composables/useContactQuery.js';
import useCompanyQuery from '../composables/useCompanyQuery.js';
import useContactMutate from '../composables/useContactMutate.js';
import useCompanyMutate from '../composables/useCompanyMutate.js';
import useDecryptSymbols from '../composables/useDecryptSymbols.js';
import useSaveProtect from '../composables/useSaveProtect.js';
import { phoneTypes } from '../composables/usePhoneTypes.js';
import CompanyInformation from './CompanyInformation.vue';
import IndividualInformation from './IndividualInformation.vue';
import Status from './Status.vue';
import LinkContactToCompany from './LinkContactToCompany.vue';
import Confirmation from './Confirmation.vue';
import LinkedContactsTab from './LinkedContactsTab.vue';
import DiaryTab from './DiaryTab.vue';
import LeadsTab from './LeadsTab.vue';
import QuotesTab from './QuotesTab.vue';
import RemindersTab from './RemindersTab.vue';
import FilesTab from './FilesTab.vue';
import { useRouter } from 'vue-router';
import useAlerts from '../composables/useAlerts.js';
import useTempSettings from '../composables/useTempSettings.js';
import useCompaniesQuery from '../composables/useCompaniesQuery.js';
import useCurrentUser from '../composables/useCurrentUser.js';
import useReminderMutate from '../composables/useReminderMutate.js';

const RETAIL_PRICING_LEVEL_ID = '9354b9f5-572a-4665-ae4d-c9ce0e78aa70';

export default {
  name: 'Contact',
  components: {
    CompanyInformation,
    IndividualInformation,
    Status,
    LinkContactToCompany,
    Confirmation,
    LinkedContactsTab,
    DiaryTab,
    LeadsTab,
    QuotesTab,
    RemindersTab,
    FilesTab,
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['company', 'contact'].includes(value),
    },
    entityid: {
      type: String,
      required: false,
      default: null,
    },
  },
  emits: ['finishedSaving'],
  setup(props, { emit }) {
    const queryCompany = useCompanyQuery();
    const queryContact = useContactQuery();
    const { addCompany, updateCompany } = useCompanyMutate();
    const { addContact, updateContact } = useContactMutate();
    const { decryptSymbols, encryptSymbols } = useDecryptSymbols();
    const router = useRouter();
    const { addAlert } = useAlerts();
    const { state: saveProtectState } = useSaveProtect();
    const { setLastContact } = useTempSettings();
    const { user } = useCurrentUser();
    const { addReminder } = useReminderMutate();

    // Reactive refs
    const companyQuery = ref(null);
    const contactQuery = ref(null);
    const data = ref(null);
    const originalData = ref(null);
    const isDirty = ref(false);
    const isSaving = ref(false);
    const showValidationErrors = ref(false);
    const activeTab = ref(null);
    const showAddContactMenu = ref(false);

    // Computed properties
    const activeQuery = computed(() => {
      return props.type === 'company' ? companyQuery.value : contactQuery.value;
    });

    const loading = computed(() => {
      return activeQuery.value?.loading || false;
    });

    const error = computed(() => {
      return activeQuery.value?.error || null;
    });

    // Execute query function
    const executeQuery = () => {
      showValidationErrors.value = false;

      if (!props.entityid) {
        companyQuery.value = null;
        contactQuery.value = null;
        data.value = null;
        return;
      }

      // Handle 'new' case - create empty objects instead of fetching
      if (props.entityid === 'new') {
        companyQuery.value = null;
        contactQuery.value = null;

        data.value = props.type === 'contact' ? { PRICINGLEVELID: RETAIL_PRICING_LEVEL_ID } : {};
        if (router.currentRoute.value.query.linkedCompanyId) {
          data.value.company = {
            COMPANYID: router.currentRoute.value.query.linkedCompanyId,
            NAME: router.currentRoute.value.query.linkedCompanyName,
          };

          data.value.COMPANYID = router.currentRoute.value.query.linkedCompanyId;
        }
        originalData.value = JSON.parse(JSON.stringify(data.value));
        isDirty.value = false;
        return;
      }

      if (props.type === 'company') {
        companyQuery.value = queryCompany(props.entityid);
        contactQuery.value = null;
      } else if (props.type === 'contact') {
        contactQuery.value = queryContact(props.entityid);
        companyQuery.value = null;
      }
    };

    // Verify function to validate required fields
    const verifyRequiredFields = () => {
      if (!data.value) {
        addAlert('No data to save', 'high', 3);
        return false;
      }

      // Company-specific validations
      if (props.type === 'company') {
        // Company name is required
        if (!data.value.NAME || data.value.NAME.trim() === '') {
          addAlert('Company name is required', 'high', 3);
          return false;
        }

        // Main phone is required
        const mainPhone = data.value.phones?.find((phone) => phone.TYPEID === phoneTypes.company.main);
        if (!mainPhone || !mainPhone.NUMBERDISPLAY || mainPhone.NUMBERDISPLAY.trim() === '') {
          addAlert('Main phone is required', 'high', 3);
          return false;
        }
      }

      // Contact-specific validations
      if (props.type === 'contact') {
        // First name is required
        if ((!data.value.FIRSTNAME || data.value.FIRSTNAME.trim() === '') && !data.value.NA) {
          addAlert('First name is required', 'high', 3);
          return false;
        }

        // Last name is required
        if ((!data.value.LASTNAME || data.value.LASTNAME.trim() === '') && !data.value.NA) {
          addAlert('Last name is required', 'high', 3);
          return false;
        }

        if (data.value.NA && !data.value.company?.COMPANYID && !data.value.COMPANYID) {
          addAlert('Company is required for departments', 'high', 3);
          return false;
        }

        // Either direct phone or mobile phone is required
        const directPhone = data.value.phones?.find((phone) => phone.TYPEID === phoneTypes.contact.direct);
        const mobilePhone = data.value.phones?.find((phone) => phone.TYPEID === phoneTypes.contact.mobile);

        const hasDirectPhone = directPhone && directPhone.NUMBERDISPLAY && directPhone.NUMBERDISPLAY.trim() !== '';
        const hasMobilePhone = mobilePhone && mobilePhone.NUMBERDISPLAY && mobilePhone.NUMBERDISPLAY.trim() !== '';

        if (data.value.NA && !data.value?.DEPARTMENTS?.length) {
          addAlert('Department is required for departments', 'high', 3);
          return false;
        }

        if (data.value.NA && !hasDirectPhone) {
          addAlert('Direct phone is required for departments', 'high', 3);
          return false;
        } else if (!hasDirectPhone && !hasMobilePhone && !data.value.NA) {
          addAlert('Either direct phone or mobile phone is required', 'high', 3);
          return false;
        }
      }

      // Category-based validations
      const category = data.value.CATEGORY;

      // Check category is required for both company and contact
      if (!category && !data.value.company?.COMPANYID && !data.value.COMPANYID) {
        addAlert('Category is required', 'high', 3);
        return false;
      } else if (!data.value.company?.COMPANYID && !data.value.COMPANYID) {
        if (category && category.includes('Customer')) {
          // Customer details is required
          if (!data.value.CUSTOMERDETAILS || data.value.CUSTOMERDETAILS?.length === 0) {
            addAlert('Customer details is required for customers', 'high', 3);
            return false;
          }

          // Address fields are required for customer companies
          if (props.type === 'company') {
            if (!data.value.address?.LINE1 || data.value.address.LINE1.trim() === '') {
              addAlert('Address line 1 is required for customers', 'high', 3);
              return false;
            }
            if (!data.value.address?.CITY || data.value.address.CITY.trim() === '') {
              addAlert('City is required for customers', 'high', 3);
              return false;
            }
            if (!data.value.address?.STATE || data.value.address.STATE.trim() === '') {
              addAlert('State is required for customers', 'high', 3);
              return false;
            }
            if (!data.value.address?.POSTALCODE || data.value.address.POSTALCODE.trim() === '') {
              addAlert('Zip is required for customers', 'high', 3);
              return false;
            }
          }
        }

        if (category && category.includes('Vendor')) {
          // Vendor details is required
          if (!data.value.VENDORDETAILS || data.value.VENDORDETAILS?.length === 0) {
            addAlert('Vendor details is required for vendors', 'high', 3);
            return false;
          }

          // Vendor dept is required
          if (!data.value.VENDORDEPT || data.value.VENDORDEPT?.length === 0) {
            addAlert('Vendor department is required for vendors', 'high', 3);
            return false;
          }
        }
      }

      return true;
    };

    // Save function
    const saveContact = async (dontVerifyOrShowAlert = false) => {
      if (!data.value) {
        return false;
      }

      // Verify required fields before saving
      if (!dontVerifyOrShowAlert && !verifyRequiredFields()) {
        showValidationErrors.value = true;
        emit('finishedSaving');
        return false;
      }

      showValidationErrors.value = false;
      isSaving.value = true;

      try {
        const dataToSave = JSON.parse(JSON.stringify(data.value));

        if (dataToSave.contacts) {
          delete dataToSave.contacts;
        }

        if ('company' in dataToSave) {
          delete dataToSave.company;
        }

        if (props.type === 'company' && 'CMSID' in dataToSave) {
          delete dataToSave.CMSID;
        }

        if (props.type === 'company') {
          delete dataToSave.TITLES;
          delete dataToSave.DEPARTMENTS;
        }

        // Only send INACTIVE when it was changed here, so a stale page can't undo a change made elsewhere
        if (!!dataToSave.INACTIVE === !!originalData.value?.INACTIVE) {
          delete dataToSave.INACTIVE;
        }

        if (dataToSave.INSIDESALES && dataToSave.INSIDESALES !== originalData.value.INSIDESALES && dataToSave.INSIDESALES !== user.userid) {
          // create reminder
          addReminder({
            TYPE: `Assigned ${props.type === 'company' ? 'Company' : 'Contact'}`,
            ASSIGNEDID: dataToSave.INSIDESALES,
            CONTACTID: props.type === 'company' ? null : props.entityid,
            COMPANYID: props.type === 'company' ? props.entityid : null,
            COMPLETED: false,
            TEXT: `You are now the assigned inside salesperson for ${props.type === 'company' ? dataToSave.NAME : ((dataToSave.FIRSTNAME || '') + ' ' + (dataToSave.LASTNAME || '')).trim()}.`,
            DATE: new Date().toISOString(),
          });
        }
        if (dataToSave.OUTSIDESALES && dataToSave.OUTSIDESALES !== originalData.value.OUTSIDESALES && dataToSave.OUTSIDESALES !== user.userid) {
          // create reminder
          addReminder({
            TYPE: `Assigned ${props.type === 'company' ? 'Company' : 'Contact'}`,
            ASSIGNEDID: dataToSave.OUTSIDESALES,
            CONTACTID: props.type === 'company' ? null : props.entityid,
            COMPANYID: props.type === 'company' ? props.entityid : null,
            COMPLETED: false,
            TEXT: `You are now the assigned outside salesperson for ${props.type === 'company' ? dataToSave.NAME : ((dataToSave.FIRSTNAME || '') + ' ' + (dataToSave.LASTNAME || '')).trim()}.`,
            DATE: new Date().toISOString(),
          });
        }

        const cleanData = removeTypenames(dataToSave);
        const encryptedData = encryptSymbols(cleanData);

        if (props.entityid === 'new') {
          if (props.type === 'company') {
            const response = await addCompany(encryptedData);
            // Reset dirty state and update original data for successful save
            isDirty.value = false;
            originalData.value = JSON.parse(JSON.stringify(data.value));
            router.replace({
              name: 'companies',
              params: {
                companyid: response.addCompany,
              },
            });
          } else if (props.type === 'contact') {
            const response = await addContact(encryptedData);
            // Reset dirty state and update original data for successful save
            isDirty.value = false;
            originalData.value = JSON.parse(JSON.stringify(data.value));
            router.replace({
              name: 'contacts',
              params: {
                contactid: response.addContact,
              },
            });
          }
        } else {
          if (props.type === 'company') {
            await updateCompany(props.entityid, encryptedData);
          } else if (props.type === 'contact') {
            await updateContact(props.entityid, encryptedData);
          }
          // Reset dirty state and update original data for successful save
          isDirty.value = false;
          originalData.value = JSON.parse(JSON.stringify(data.value));
        }

        if (!dontVerifyOrShowAlert) {
          addAlert(props.type === 'company' ? 'Company saved successfully' : 'Contact saved successfully', 'low', 2);
        }
        emit('finishedSaving');
        isSaving.value = false;
        return true;
      } catch (error) {
        console.error('Error saving contact:', error);
        emit('finishedSaving');
        isSaving.value = false;
        return false;
      }
    };

    // Helper function to remove __typename fields recursively
    const removeTypenames = (obj) => {
      if (Array.isArray(obj)) {
        return obj.map(removeTypenames);
      } else if (obj !== null && typeof obj === 'object') {
        const newObj = {};
        for (const key in obj) {
          if (key !== '__typename') {
            newObj[key] = removeTypenames(obj[key]);
          }
        }
        return newObj;
      }
      return obj;
    };

    // Watch for props changes
    watch(() => [props.type, props.entityid], executeQuery, { immediate: true });

    // Watch for query result changes and update data ref
    watch(
      () => {
        if (props.type === 'company' && companyQuery.value?.result?.company) {
          return companyQuery.value.result.company;
        } else if (props.type === 'contact' && contactQuery.value?.result?.contact) {
          return contactQuery.value.result.contact;
        }
        return null;
      },
      (newResult) => {
        if (newResult) {
          const decryptedData = decryptSymbols(newResult);
          // MultiDropdowns require arrays; the form initializes null → [] on mount.
          // TITLES/DEPARTMENTS are contact-only and must not be added to company data.
          if (props.type === 'contact') {
            if (!decryptedData.TITLES) decryptedData.TITLES = [];
            if (!decryptedData.DEPARTMENTS) decryptedData.DEPARTMENTS = [];
          }
          data.value = decryptedData;
          originalData.value = JSON.parse(JSON.stringify(decryptedData));
          isDirty.value = false;
        } else if (props.entityid !== 'new') {
          data.value = null;
          originalData.value = null;
          isDirty.value = false;
        }
        activeTab.value = props.type === 'company' ? 'linkedContacts' : 'diary';
        showAddContactMenu.value = false;
      },
      { immediate: true, deep: true }
    );

    // Watch for changes in data to set isDirty
    watch(
      data,
      (newData) => {
        if (props.entityid === 'new' && newData) {
          const hasAnyData = (function checkForData(obj) {
            if (obj === null || obj === undefined || obj === '') {
              return false;
            }

            if (Array.isArray(obj)) {
              return obj.length > 0 && obj.some((item) => checkForData(item));
            }

            if (typeof obj === 'object') {
              for (const key in obj) {
                if (key === 'TYPEID') continue;
                if (key === '__typename') continue;
                if (key === 'PRICINGLEVELID' && obj[key] === RETAIL_PRICING_LEVEL_ID) continue;
                if (checkForData(obj[key])) return true;
              }
              return false;
            }

            return true;
          })(newData);

          isDirty.value = hasAnyData;
        } else if (newData && originalData.value) {
          const newDataCopy = JSON.parse(JSON.stringify(newData));
          const originalDataCopy = JSON.parse(JSON.stringify(originalData.value));

          // compare phones;
          const newPhones =
            newDataCopy?.phones?.map((phone) => {
              return { num: phone.NUMBERDISPLAY?.replace(/[^0-9]/g, '') || '', type: phone.TYPEID };
            }) || [];

          const originalPhones =
            originalDataCopy?.phones?.map((phone) => {
              return { num: phone.NUMBERDISPLAY?.replace(/[^0-9]/g, '') || '', type: phone.TYPEID };
            }) || [];

          const isPhoneChanged = newPhones.some((phone) => !originalPhones.some((p) => p.num === phone.num && p.type === phone.type) && originalPhones.some((p) => p.type === phone.type));

          if (isPhoneChanged) {
            isDirty.value = true;
          } else {
            // delete newData.phones and originalData.value.phones
            delete newDataCopy.phones;
            delete originalDataCopy.phones;

            // remove company from newData and originalData.value
            delete newDataCopy.company;
            delete originalDataCopy.company;

            if (originalDataCopy.address === null) {
              originalDataCopy.address = {};
            }

            if (!originalDataCopy.TITLES) originalDataCopy.TITLES = [];
            if (!originalDataCopy.DEPARTMENTS) originalDataCopy.DEPARTMENTS = [];
            if (!newDataCopy.TITLES) newDataCopy.TITLES = [];
            if (!newDataCopy.DEPARTMENTS) newDataCopy.DEPARTMENTS = [];

            const isDataChanged = JSON.stringify(newDataCopy) !== JSON.stringify(originalDataCopy);

            isDirty.value = isDataChanged;
          }
        } else {
          isDirty.value = false;
        }
      },
      { deep: true }
    );

    // Watch isDirty and update saveProtectState
    watch(isDirty, (val) => {
      saveProtectState.isDirty = val;
    });

    // Watch for errors and handle them with alerts + navigation
    watch(error, (errorValue) => {
      if (errorValue) {
        setLastContact(null, null);
        router.replace({ name: 'companies', params: null });
      }
    });

    return {
      queryCompany,
      queryContact,
      updateCompany,
      updateContact,
      decryptSymbols,
      encryptSymbols,
      companyQuery,
      contactQuery,
      data,
      originalData,
      isDirty,
      activeQuery,
      loading,
      error,
      isSaving,
      executeQuery,
      saveContact,
      verifyRequiredFields,
      addAlert,
      showValidationErrors,
      saveProtectState,
      activeTab,
      showAddContactMenu,
      companies: useCompaniesQuery.companies,
      user,
    };
  },
  data() {
    return {
      showLinkCompany: false,
      showUnlinkConfirm: false,
      hasCheckedEmails: false,
    };
  },
  methods: {
    closeAddContactMenu() {
      this.showAddContactMenu = false;
    },
    confirmUnlinkCompany() {
      this.showUnlinkConfirm = true;
    },
    handleContactStatusChanged({ contactid, inactive }) {
      // Keep the company's contact list current without refetching (which would discard unsaved edits).
      // Both copies are patched so the form isn't marked dirty.
      [this.data?.contacts, this.originalData?.contacts].forEach((contacts) => {
        const contact = contacts?.find((contact) => contact.CONTACTID === contactid);
        if (contact) contact.INACTIVE = inactive;
      });
    },
    async handleLinkCompany(selectedCompany) {
      if (!this.data || !selectedCompany) {
        this.showLinkCompany = false;
        return;
      }

      try {
        // Link the company
        this.data.COMPANYID = selectedCompany.value;

        // Clear all status fields
        this.clearStatusFields();

        this.showLinkCompany = false;

        if (this.entityid === 'new') {
          const uppercaseId = selectedCompany.value.toUpperCase();
          this.data.company = {
            NAME: this.companies.find((company) => company.COMPANYID === uppercaseId)?.NAME,
            COMPANYID: uppercaseId,
          };
        } else {
          const success = await this.saveContact(true);

          this.executeQuery();

          if (!success) {
            throw new Error('Save Contact Failed');
          }
        }

        this.addAlert('Contact successfully linked to company', 'low', 2);
      } catch (error) {
        console.error('Error linking company to contact:', error);
        this.addAlert('Error linking company to contact', 'high', 5);
      }
    },
    async handleUnlinkCompany() {
      if (!this.data) {
        this.showUnlinkConfirm = false;
        return;
      }

      try {
        // Remove the company link
        this.data.COMPANYID = null;
        delete this.data.company;

        this.showUnlinkConfirm = false;

        if (this.entityid !== 'new') {
          // Save the contact without the company link
          const success = await this.saveContact(true);

          // Refresh the contact data
          this.executeQuery();

          if (!success) {
            throw new Error('Save Contact Failed');
          }
        }

        this.addAlert('Contact successfully unlinked from company', 'low', 2);
      } catch (error) {
        console.error('Error unlinking company from contact:', error);
        this.addAlert('Error unlinking company from contact', 'high', 5);
      }
    },
    clearStatusFields() {
      if (!this.data) return;

      // Clear all status-related fields
      this.data.CATEGORY = null;
      this.data.INSIDESALES = null;
      this.data.OUTSIDESALES = null;
      this.data.CUSTOMERDETAILS = [];
      this.data.VENDORDEPT = [];
      this.data.VENDORDETAILS = [];

      // Clear contact-specific fields
      if (this.type === 'contact') {
        this.data.SALESCONTACT = false;
      }

      // Clear company-specific fields
      if (this.type === 'company') {
        this.data.CERTIFIEDCONTRACTOR = false;
      }
    },
    shareContact() {
      // Create a mailto link with contact information in the body
      if (!this.data) return;

      let name = '';
      let phone = '';
      let email = '';
      let website = '';

      // Handle different data structures for company vs contact
      if (this.type === 'company') {
        name = this.data.NAME || '';
        phone = this.data.phones?.find((phone) => phone.TYPEID === phoneTypes.company.main)?.NUMBERDISPLAY || '';
        website = this.data.WEBADDRESS || '';
      } else if (this.type === 'contact') {
        name = `${this.data.FIRSTNAME || ''} ${this.data.LASTNAME || ''}`.trim();

        // For contacts, try direct phone first, then mobile phone
        const directPhone = this.data.phones?.find((phone) => phone.TYPEID === phoneTypes.contact.direct)?.NUMBERDISPLAY || '';
        const mobilePhone = this.data.phones?.find((phone) => phone.TYPEID === phoneTypes.contact.mobile)?.NUMBERDISPLAY || '';
        phone = directPhone || mobilePhone;

        email = this.data.email?.ADDRESS || '';

        // For contacts, get website from company if linked
        website = this.data.company?.WEBADDRESS || '';
      }

      // Build address string (same for both types)
      const address = [
        this.data?.address?.LINE1 || '',
        this.data?.address?.LINE2 || '',
        this.data?.address?.CITY ? `${this.data?.address?.CITY}, ${this.data?.address?.STATE || ''} ${this.data?.address?.POSTALCODE || ''}` : '',
      ]
        .filter(Boolean)
        .join(', ');

      // Build the email body with available information
      let bodyParts = [];
      if (name) bodyParts.push(`Name: ${name}`);
      if (phone) bodyParts.push(`Phone: ${phone}`);
      if (email) bodyParts.push(`Email: ${email}`);
      if (address) bodyParts.push(`Address: ${address}`);
      if (website) bodyParts.push(`Website: ${website}`);

      const body = encodeURIComponent(`\n\n${bodyParts.join('\n')}`);

      // Create and open the mailto link
      const mailtoLink = `mailto:?body=${body}`;
      window.location.href = mailtoLink;
    },
    linkExistingContact() {
      // Close the add contact menu first
      this.showAddContactMenu = false;

      // Call the method on the LinkedContactsTab component to show the popup
      if (this.$refs.linkedContactsTab) {
        this.$refs.linkedContactsTab.showLinkExistingContactPopup();
      }
    },
    addDiaryNote() {
      // Call the method on the DiaryTab component to show the add diary note popup
      if (this.$refs.diaryTab) {
        this.$refs.diaryTab.openAddDiaryNote();
      }
    },
    addReminder() {
      // Call the method on the RemindersTab component to show the add reminder popup
      if (this.$refs.remindersTab) {
        this.$refs.remindersTab.openAddReminder();
      }
    },
    addFiles() {
      // Call the method on the FilesTab component to show the add files popup
      this.$refs?.filesTab?.$refs?.fileUpload?.$refs?.fileUploadInput?.click();
    },
    addLead() {
      if (!this.user.insideSales) {
        this.addAlert('You are not an inside salesperson and cannot add leads', 'high', 4);
        return;
      }

      // Call the method on the LeadsTab component to show the add lead popup
      this.$refs.leadsTab.addLead();
    },
    addQuote() {
      let query;
      if (this.type === 'company') {
        query = { companyid: this.entityid };
      } else if (this.data?.NA) {
        const companyid = this.data.COMPANYID || this.data.company?.COMPANYID;
        query = { companyid, contactid: this.entityid };
      } else {
        query = { contactid: this.entityid };
      }

      this.$router.push({ name: 'quote', params: { quoteid: 'new' }, query });
    },
  },
};
</script>

<style scoped lang="scss">
.contact-wrapper {
  padding: 0;

  .loading {
    width: 100%;
    max-height: 100%;
    min-height: 15rem;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 2rem;
      height: 2rem;
    }
  }

  .error {
    text-align: center;
    padding: 2rem;
    color: #d32f2f;
    background-color: #ffebee;
    border-radius: 4px;
  }

  .contact-content {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding: 3rem 3.75rem;
    gap: 4rem;
    width: 100%;
    justify-content: flex-start;
    max-width: fit-content;
  }

  .tabs {
    background: $appBackgroundColor;

    .left-space {
      width: 3.75rem;
      padding: 0;
    }

    .buttons-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      gap: 1rem;
      padding: 0 2rem;
    }
  }
}

.add-contact-btn {
  position: relative;

  .add-contact-menu {
    top: 3rem;
    right: 0rem;

    li {
      color: $textColor87;
      text-transform: none;
      font-size: $fontSize14;
      font-size: 1rem;
      font-weight: 400;
      padding: 0.5rem 0.8rem;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 0.3rem;

      img {
        height: 1.1rem;
      }

      &:hover {
        background-color: $hoverGrayColor;
      }
    }
  }
}
</style>
